import React, { useEffect } from 'react';
import { useActivation } from '@context/MobileOne';
import { useA11y } from '@context/Utils/A11y';
import { OverviewData } from './OverviewData';
import { OverviewConsent } from './OverviewConsent';
import { OverviewSuccess } from './OverviewSuccess';
import { OverviewError } from './OverviewError';
import { EditPrivateData } from './EditData/EditPrivateData';
import { EditAddressData } from './EditData/EditAddressData';
import { EditMnpData } from './EditData/EditMnpData';
import { EditLegitimation } from './EditData/EditLegitimation';
import { PDFDownload } from './PDFDownload';
import { Meta } from '@core/Meta';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTitle } from '@context/Utils/Title';

export function ActivationStep9() {
  // Context
  const { step9SubStep, setStep9SubStep, setShowActivationFooter, setShowActivationHeader } = useActivation();
  const { tA11yTranslate, getGlobalMetaTitles } = useA11y();
  const location = useLocation();
  const navigate = useNavigate();
  const { announceTitle } = useTitle();

  // META: page titles
  const { globalPageTitle, globalSuccessPageTitle, globalErrorPageTitle } = getGlobalMetaTitles();
  const activationOverviewPageTitle = tA11yTranslate('ek_global_meta_title_overview').content;
  const activationOverviewConsentPageTitle = tA11yTranslate(
    'ek_global_meta_title_overview_consent'
  ).content;
  const changePersonalDataPageTitle = tA11yTranslate(
    'ek_global_meta_title_change_pers_data'
  ).content;
  const changePersonalDataAddressPageTitle = tA11yTranslate(
    'ek_global_meta_title_change_address'
  ).content;
  const changeMnpPageTitle = tA11yTranslate('ek_global_meta_title_change_mnp').content;
  const changeLegitimationPageTitle = tA11yTranslate(
    'ek_global_meta_title_change_legitimation'
  ).content;
  const legalDocumentsPageTitle = tA11yTranslate('ek_global_meta_title_legal_documents').content; // TODO: MISSING IN FIGMA

  // Determine the page title based on the current substep
const getPageTitle = () => {
  switch (step9SubStep) {
    case 0:
      return `${globalPageTitle} - ${activationOverviewPageTitle}`;
    case 1:
      return `${globalPageTitle} - ${activationOverviewConsentPageTitle}`;
    case 2:
      return `${globalPageTitle} - ${legalDocumentsPageTitle}`;
    case 3:
      return `${globalPageTitle} - ${globalSuccessPageTitle}`;
    case 4:
      return `${globalPageTitle} - ${globalErrorPageTitle}`;
    case 5:
      return `${globalPageTitle} - ${changePersonalDataPageTitle}`;
    case 6:
      return `${globalPageTitle} - ${changePersonalDataAddressPageTitle}`;
    case 7:
      return `${globalPageTitle} - ${changeMnpPageTitle}`;
    case 8:
      return `${globalPageTitle} - ${changeLegitimationPageTitle}`;
    default:
      return `${globalPageTitle} - ${activationOverviewPageTitle}`;
  }
};

  useEffect(() => {
    const pageTitle = `${getPageTitle()}`;
    // read titles only if they are defined in this file
    if (pageTitle !== '') {
      announceTitle(pageTitle);
    }
  }, [step9SubStep, announceTitle]); // Re-run when substep changes

  // Hooks
  useEffect(() => {
    // reset substep handling
    setStep9SubStep(0);
  }, []);

  // Add after your other useEffects (around line 87):
useEffect(() => {
  // Control header/footer visibility based on substep
  if (step9SubStep === 2 || step9SubStep === 3 || step9SubStep === 4) {
    // Hide for: PDFDownload, OverviewSuccess, OverviewError
    setShowActivationFooter(false);
    setShowActivationHeader(false);
  } else {
    // Show for: OverviewData, OverviewConsent, and Edit substeps
    setShowActivationFooter(true);
    setShowActivationHeader(true);
  }
}, [step9SubStep]);

  useEffect(() => {
    if (location.state?.key === 1) {
      setStep9SubStep(location.state.key);
      navigate(location.pathname, { replace: true });
    }
  }, [location]);

  const renderStep9SubSteps = (subStep) => {
    switch (subStep) {
      case 0:
        return <OverviewData />;
      case 1:
        return <OverviewConsent />;
      case 2:
        return <PDFDownload />;
      case 3:
        return <OverviewSuccess />;
      case 4:
        return <OverviewError />;
      case 5:
        return <EditPrivateData />;
      case 6:
        return <EditAddressData />;
      case 7:
        return <EditMnpData />;
      case 8:
        return <EditLegitimation />;
      default:
        return <OverviewData />;
    }
  };

  // return renderStep9SubSteps(step9SubStep);
  return (
    <>
      <Meta title={getPageTitle()} key={`meta-step-${step9SubStep}`} />
      {renderStep9SubSteps(step9SubStep)}
    </>
  );
}

export default ActivationStep9;

// UNUSED - used before mps implementation
// // import React, { useEffect } from 'react';
// import { useActivation } from '@context/MobileOne';
// import { useA11y } from '@context/Utils/A11y';
// import { Payment } from './OLD_Payment';
// import { PaymentAmount } from './OLD_PaymentAmount';
// import { AutoTopupInitialization } from './OLD_AutoTopupInitialization';
// import { HowItWorks } from './OLD_HowItWorks';
// import { Meta } from '@core/Meta';
// import { keyValueValidation } from '@utils/globalConstant';
// import { useTitle } from '@context/Utils/Title';
// export function ActivationStep9() {
//   // Context
//   const { step9SubStep, setShowActivationFooter, setShowActivationHeader } = useActivation();
//   const { tA11yTranslate, getGlobalMetaTitles } = useA11y();
//   const { announceTitle } = useTitle();
//   // META: page titles
//   const { globalPageTitle } = getGlobalMetaTitles();
//   const activationRegTopUpPageTitle = tA11yTranslate('ek_global_meta_title_reg_top_up').content;
//   const activationRegTopUpDetailsPageTitle = tA11yTranslate('ek_global_meta_title_reg_top_up_details').content;
//   const activationRegTopUpChooseAmountPageTitle = tA11yTranslate('ek_global_meta_title_reg_top_up_choose_amount').content;
//   const activationRegTopUpPaymentPageTitle = tA11yTranslate('ek_global_meta_title_reg_top_up_payment').content;

//   const isTranslationReady = keyValueValidation(
//     activationRegTopUpPageTitle,
//     'ek_global_meta_title_reg_top_up'
//   );

//   useEffect(() => {
//     setShowActivationFooter(true);
//     setShowActivationHeader(true);

//     if (step9SubStep === 1) {
//       setShowActivationFooter(false);
//       setShowActivationHeader(false);
//     }
//   }, [step9SubStep]);

//   // Determine the page title based on the current substep
//   const getPageTitle = () => {
//     if (step9SubStep === 1) {
//       return `${globalPageTitle} - ${activationRegTopUpDetailsPageTitle}`;
//     } else if(step9SubStep === 2){
//       return `${globalPageTitle} - ${activationRegTopUpChooseAmountPageTitle}`;
//     } else if(step9SubStep === 3){
//       return `${globalPageTitle} - ${activationRegTopUpPaymentPageTitle}`;
//     } else{
//       return `${globalPageTitle} - ${activationRegTopUpPageTitle}`;
//     }
//   };

//   useEffect(() => {
//     const pageTitle = `${getPageTitle()}`;
//     announceTitle(pageTitle);
//   }, [step9SubStep, announceTitle]); // Re-run when substep changes

//   const renderStep9SubSteps = (subStep) => {
//     switch (subStep) {
//       case 0:
//         return <AutoTopupInitialization />;
//       case 1:
//         return <HowItWorks />;
//       case 2:
//         return <PaymentAmount />;
//       case 3:
//         return <Payment />;
//       default:
//         return <AutoTopupInitialization />;
//     }
//   };

//   return (
//     <>
//       <Meta title={getPageTitle()} key={`meta-key-${step9SubStep}`} />
//       {renderStep9SubSteps(step9SubStep)}
//     </>
//   );
// }

// export default ActivationStep9;
